import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  CircularProgress, 
  Button,
  Breadcrumbs,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from '../components/Layout';
import UserRatingsList from '../components/UserRatingsList';
import { getUserWithPhotoDetails } from '../services/users';

const UserRatingsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch user details when the component loads
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError('');
      
      try {
        const userData = await getUserWithPhotoDetails(userId);
        if (userData) {
          setUser(userData);
        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserDetails();
  }, [userId]);

  // Handle back button navigation
  const handleBack = () => {
    navigate(-1); // This mimics the browser's back button
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ mt: 3, color: 'error.main' }}>
            <Typography variant="h5">Error</Typography>
            <Typography variant="body1">{error}</Typography>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg">
        {/* Header and Navigation */}
        <Box sx={{ mb: 3, mt: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={handleBack}
            >
              User Details
            </Link>
            <Typography color="text.primary">Profile Ratings</Typography>
          </Breadcrumbs>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ mr: 2 }}
              >
                Back
              </Button>
              <Typography variant="h4" component="h1">
                {user ? `${user.profile?.name || 'User'}'s Profile Ratings` : 'User Ratings'}
              </Typography>
            </Box>
            {user && (
              <Typography variant="subtitle1" color="text.secondary">
                @{user.profile?.userName || user.id}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Content */}
        <UserRatingsList userId={userId} />
      </Container>
    </Layout>
  );
};

export default UserRatingsPage;